import { useEffect, useState } from "react";
import { getDurableConversation } from "@/api/conversations";
import { getTaskWorker } from "@/api/task-workers";
import { conversationActionAllowed } from "@/components/panels/conversation-status";
import { useProjectStore } from "@/stores/project-store";
import type { WorkToastItem } from "@/stores/work-toast-store";

/** Spec §6.4: Done offers a follow-up only for this project's agent or native
 *  worker, and only while the runtime still accepts one. Probed once per state. */
export function useWorkToastFollowUp(item: WorkToastItem): boolean {
  const currentProjectId = useProjectStore((state) => state.currentProject?.id ?? null);
  const { key, state, source, projectId, taskId, conversationId, stateChangedAt } = item;
  const eligible =
    state === "done" &&
    (source === "agent" || source === "native_worker") &&
    conversationId !== null &&
    projectId !== null &&
    projectId === currentProjectId &&
    (source !== "native_worker" || taskId !== null);
  const probeKey = eligible ? `${key}:${stateChangedAt}` : null;
  const [probed, setProbed] = useState<{ key: string; allowed: boolean } | null>(null);

  useEffect(() => {
    if (probeKey === null || projectId === null || conversationId === null) return;
    let cancelled = false;
    const probe =
      source === "native_worker" && taskId !== null
        ? getTaskWorker(projectId, taskId).then((worker) => worker?.can_instruct === true)
        : getDurableConversation(conversationId).then((conversation) =>
            conversationActionAllowed(conversation, "resume", conversation.status === "active"),
          );
    void probe.then(
      (allowed) => {
        if (!cancelled) setProbed({ key: probeKey, allowed });
      },
      () => {
        if (!cancelled) setProbed({ key: probeKey, allowed: false });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [conversationId, probeKey, projectId, source, taskId]);

  return probeKey !== null && probed?.key === probeKey && probed.allowed;
}
